// =====================================================================
//  COMPONENTES BASE
//
//  Emiten el mismo DOM y las mismas clases que las plantillas de texto del
//  prototipo, para que el CSS levantado verbatim funcione sin retoques.
// =====================================================================

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { colorPuntaje } from '../dominio/formato'
import { leerError, type ErrorLegible } from '../lib/errores'

// ---------------------------------------------------------------------
//  Etiquetas y barras
// ---------------------------------------------------------------------

export function Tag({ clase, children }: { clase: string; children: ReactNode }) {
  return <span className={`tag ${clase}`}>{children}</span>
}

export function Barra({
  pct,
  ancho = '52px',
  alto,
  color,
}: {
  pct: number
  ancho?: string
  alto?: string
  color?: string
}) {
  return (
    <div className="bar" style={{ width: ancho, ...(alto ? { height: alto } : {}) }}>
      <i style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color ?? colorPuntaje(pct) }} />
    </div>
  )
}

/** La barra de puntaje con el número al lado, como en el listado. */
export function Puntaje({ valor, ancho = '52px' }: { valor: number; ancho?: string }) {
  return (
    <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
      <Barra pct={valor} ancho={ancho} />
      <b style={{ fontSize: 12 }}>{valor}</b>
    </div>
  )
}

// ---------------------------------------------------------------------
//  Tarjetas y avisos
// ---------------------------------------------------------------------

export function Kpi({
  etiqueta,
  valor,
  pie,
  color,
}: {
  etiqueta: string
  valor: string
  pie?: ReactNode
  color?: string
}) {
  return (
    <div className="card kpi">
      <div className="lbl">{etiqueta}</div>
      <div className="val" style={color ? { color } : undefined}>
        {valor}
      </div>
      {pie ? <div className="foot">{pie}</div> : null}
    </div>
  )
}

export type TonoAlerta = 'info' | 'warn' | 'bad' | 'ok'

export function Alerta({
  tono,
  children,
  estilo,
}: {
  tono: TonoAlerta
  children: ReactNode
  estilo?: React.CSSProperties
}) {
  return (
    <div className={`alert a-${tono}`} style={estilo}>
      {children}
    </div>
  )
}

/** Los recuadros de cuenta regresiva del panel. */
export function Cuenta({ datos }: { datos: Array<[string | number, string]> }) {
  return (
    <div className="cd" style={{ marginBottom: 18 }}>
      {datos.map(([v, t]) => (
        <div key={t}>
          <b>{v}</b>
          <span>{t}</span>
        </div>
      ))}
    </div>
  )
}

export function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="sect">
      <h4>{titulo}</h4>
      {children}
    </div>
  )
}

/** El encabezado de sección dentro de una .card, como en el panel. */
export function TituloCard({ children }: { children: ReactNode }) {
  return (
    <h4
      style={{
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '.7px',
        color: 'var(--acc2)',
        marginBottom: 12,
      }}
    >
      {children}
    </h4>
  )
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return <div className="cargando">{texto}</div>
}

export function ErrorCarga({ error }: { error: unknown }) {
  const e = leerError(error)
  return (
    <Alerta tono="bad">
      <b>{e.titulo}.</b> {e.detalle}
    </Alerta>
  )
}

export function Vacio({ children, columnas }: { children: ReactNode; columnas: number }) {
  return (
    <tr>
      <td colSpan={columnas} className="sub" style={{ padding: 26, textAlign: 'center' }}>
        {children}
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------
//  Campos de formulario
// ---------------------------------------------------------------------

export function Campo({
  etiqueta,
  children,
  estado,
  soloLectura,
}: {
  etiqueta: ReactNode
  children: ReactNode
  estado?: EstadoGuardado
  soloLectura?: boolean
}) {
  return (
    <div className={`field${soloLectura ? ' solo-lectura' : ''}`}>
      <label>
        {etiqueta}
        {estado === 'guardando' ? <span className="estado-campo va">guardando…</span> : null}
        {estado === 'guardado' ? <span className="estado-campo ok">guardado</span> : null}
        {estado === 'error' ? <span className="estado-campo mal">no se guardó</span> : null}
      </label>
      {children}
    </div>
  )
}

export type EstadoGuardado = 'quieto' | 'guardando' | 'guardado' | 'error'

export function Fila2({ children }: { children: ReactNode }) {
  return <div className="row2">{children}</div>
}

// ---------------------------------------------------------------------
//  Avisos flotantes — para los errores que devuelve el motor
// ---------------------------------------------------------------------

interface Aviso extends ErrorLegible {
  id: number
}

const AvisosCtx = createContext<(e: unknown) => void>(() => {})

/** Muestra el error traducido de un trigger, citando el artículo. */
export const useAvisar = () => useContext(AvisosCtx)

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([])

  const avisar = useCallback((e: unknown) => {
    const legible = leerError(e)
    const id = Date.now() + Math.random()
    setAvisos((prev) => [...prev, { ...legible, id }])
    // Las reglas legales se quedan hasta que la persona las cierre: son
    // explicaciones de por qué el sistema no aceptó algo, no notificaciones.
    if (!legible.esRegla) {
      setTimeout(() => setAvisos((prev) => prev.filter((x) => x.id !== id)), 7000)
    }
  }, [])

  const cerrar = (id: number) => setAvisos((prev) => prev.filter((x) => x.id !== id))
  const valor = useMemo(() => avisar, [avisar])

  return (
    <AvisosCtx.Provider value={valor}>
      {children}
      {avisos.length > 0 && (
        <div className="avisos">
          {avisos.map((a) => (
            <div
              key={a.id}
              className={`alert a-${a.esRegla ? 'warn' : 'bad'}`}
              onClick={() => cerrar(a.id)}
              title="Clic para cerrar"
            >
              <b>{a.titulo}</b>
              {a.articulo ? <span className="sub"> · {a.articulo}</span> : null}
              <br />
              {a.detalle}
            </div>
          ))}
        </div>
      )}
    </AvisosCtx.Provider>
  )
}
